// src/pages/TrainingLive.jsx
// Vista live di una sessione di ALLENAMENTO. Consuma i frame training_* via WS
// (autenticati con token). Mostra mappa multi-pilota + telemetria dettagliata
// per il pilota selezionato. Nessuna classifica.
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, Polyline, CircleMarker, Tooltip, ZoomControl, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../components/icons';
import { useSession } from '../session';
import './TrainingLive.css';
import { colorFromName } from '../utils/colors';
import { ThemeToggle } from '../components/ThemeContext';
import TrainingPilotPanel from '../components/telemetry/TrainingPilotPanel';
import TrainingCrew from '../components/telemetry/TrainingCrew';
import { haversine, formatLap, findDistOnCircuit } from '../components/telemetry/TelemetryCharts';
import { DeltaReadout, fmtLapTime, fmtSigned } from '../components/telemetry/TelemetryPro';

const API_BASE = process.env.REACT_APP_API_BASE || (window.location.protocol === 'https:' ? '' : `http://${window.location.hostname}:5000`);
const WS_URL = process.env.REACT_APP_WS_URL || (window.location.protocol === 'https:' ? `wss://${window.location.hostname}/ws` : `ws://${window.location.hostname}:5001`);

// Colore traiettoria per velocità (rosso lento → giallo → verde veloce),
// stesso schema del canvas circuito.
function trailSpeedColor(speed, min, max) {
  const lo = min + (max - min) * 0.05, hi = max - (max - min) * 0.05;
  const t = Math.max(0, Math.min(1, ((Number(speed) || 0) - lo) / ((hi - lo) || 1)));
  let r, g;
  if (t < 0.5) { r = 255; g = Math.round(255 * (t / 0.5)); }
  else { r = Math.round(255 * (1 - (t - 0.5) / 0.5)); g = 255; }
  return `rgb(${r},${g},0)`;
}

/* ═══════════════════════════════════════════════════════════════════════════
   VISTA SATELLITARE (solo allenamento) — mappa Leaflet con imagery Esri,
   pista + traiettorie speed-color + marker piloti live. Dati live letti dai
   ref di TrainingLive (aggiornati via WS), campionati a 10 Hz.
   ═══════════════════════════════════════════════════════════════════════════ */
// La mappa satellitare si riadatta quando cambia lo spazio (schermo intero).
function InvalidateOnResize() {
  const map = useMap();
  useEffect(() => {
    const el = map.getContainer();
    if (typeof ResizeObserver === 'undefined' || !el) return undefined;
    let raf = null;
    const ro = new ResizeObserver(() => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => { raf = null; map.invalidateSize({ pan: false }); });
    });
    ro.observe(el);
    return () => { ro.disconnect(); if (raf) cancelAnimationFrame(raf); };
  }, [map]);
  return null;
}

// Ricentra la mappa sul pilota seguito (SEGUI POSIZIONE) ad ogni aggiornamento.
function SatelliteFollow({ target }) {
  const map = useMap();
  useEffect(() => {
    if (!target || !Number.isFinite(target.lat) || !Number.isFinite(target.lon)) return;
    map.setView([target.lat, target.lon], map.getZoom(), { animate: false });
  }, [target?.lat, target?.lon, map]);
  return null;
}

// Rotella del mouse: zoom solo a schermo intero. Fuori dallo schermo intero la
// rotella fa scorrere la pagina; restano i pulsanti + e −.
function WheelZoom({ enabled }) {
  const map = useMap();
  useEffect(() => {
    if (enabled) map.scrollWheelZoom.enable(); else map.scrollWheelZoom.disable();
  }, [map, enabled]);
  return null;
}

function TrainingSatelliteMap({ circuit, liveRefs, drivers, selectedMac, onSelectDriver, followSelected, wheelZoom = false }) {
  const sectors = useMemo(() => (Array.isArray(circuit?.sectors) ? circuit.sectors : []), [circuit]);
  const centerline = useMemo(() => sectors.map(s => [s.lat, s.lon]), [sectors]);
  const bounds = useMemo(() => {
    if (!sectors.length) return null;
    let minLat = Infinity, maxLat = -Infinity, minLon = Infinity, maxLon = -Infinity;
    sectors.forEach(s => {
      if (s.lat < minLat) minLat = s.lat; if (s.lat > maxLat) maxLat = s.lat;
      if (s.lon < minLon) minLon = s.lon; if (s.lon > maxLon) maxLon = s.lon;
    });
    return [[minLat, minLon], [maxLat, maxLon]];
  }, [sectors]);

  // Campionamento a 10 Hz dei dati live dai ref.
  const [pilots, setPilots] = useState([]);
  useEffect(() => {
    const { telemRef, trailsRef, colorsRef, drPhaseRef } = liveRefs;
    let timer;
    const sample = () => {
      const out = [];
      (drivers || []).forEach(d => {
        const phase = drPhaseRef.current.get(d.mac)?.phase || d.drPhase || 'LIVE';
        if (phase === 'GONE') return;
        const live = telemRef.current.get(d.mac);
        const lat = live?.lat ?? d.lat, lon = live?.lon ?? d.lon;
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
        out.push({
          mac: d.mac,
          tag: d.tag || (d.fullName || '').slice(0, 4),
          color: colorsRef.current[d.mac] || '#C0FF03',
          lat, lon,
          inPit: !!d.inPit,
          trail: (trailsRef.current[d.mac] || []).filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lon)),
        });
      });
      setPilots(out);
      timer = setTimeout(sample, 100);
    };
    sample();
    return () => clearTimeout(timer);
  }, [liveRefs, drivers]);

  // Range velocità globale per la colorazione delle traiettorie.
  const [sMin, sMax] = useMemo(() => {
    let mn = Infinity, mx = -Infinity;
    pilots.forEach(p => p.trail.forEach(pt => { const v = Number(pt.speed) || 0; if (v < mn) mn = v; if (v > mx) mx = v; }));
    if (!Number.isFinite(mn) || !Number.isFinite(mx) || mn === mx) return [0, 100];
    return [mn, mx];
  }, [pilots]);

  if (!bounds) {
    return <div className="tl-sat-empty">Geometria circuito non disponibile per la vista satellitare.</div>;
  }

  return (
    <MapContainer bounds={bounds} boundsOptions={{ padding: [30, 30] }} className="tl-sat-map" zoomControl={false} scrollWheelZoom={false}>
      {/* zoom in basso a destra: in alto a sinistra c'è il riquadro del pilota,
          a destra (schermo intero) l'elenco dei piloti */}
      <ZoomControl position="bottomright" />
      <WheelZoom enabled={wheelZoom} />
      <InvalidateOnResize />
      <TileLayer
        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
        attribution="Tiles &copy; Esri — Source: Esri, Maxar, Earthstar Geographics"
        maxNativeZoom={19}
        maxZoom={22}
      />
      {followSelected && selectedMac && (
        <SatelliteFollow target={pilots.find(p => p.mac === selectedMac) || null} />
      )}
      {/* Centerline pista (riferimento) */}
      {centerline.length > 1 && (
        <Polyline positions={centerline} pathOptions={{ color: '#ffffff', weight: 2, opacity: 0.55, dashArray: '4 5' }} />
      )}
      {/* Linea start */}
      {sectors.length > 1 && (
        <Polyline
          positions={[[sectors[0].lat, sectors[0].lon], [sectors[1].lat, sectors[1].lon]]}
          pathOptions={{ color: '#ffffff', weight: 4, opacity: 0.9 }}
        />
      )}
      {/* Traiettorie + piloti */}
      {pilots.map(p => (
        <React.Fragment key={p.mac}>
          {p.trail.length > 1 && p.trail.slice(1).map((pt, i) => (
            <Polyline
              key={i}
              positions={[[p.trail[i].lat, p.trail[i].lon], [pt.lat, pt.lon]]}
              pathOptions={{ color: trailSpeedColor(pt.speed, sMin, sMax), weight: 4, opacity: 0.95 }}
            />
          ))}
          <CircleMarker
            center={[p.lat, p.lon]}
            radius={p.mac === selectedMac ? 9 : 7}
            pathOptions={{
              color: p.inPit ? '#f59e0b' : '#ffffff',
              weight: p.mac === selectedMac ? 3 : 2,
              fillColor: p.color, fillOpacity: 1,
            }}
            eventHandlers={{ click: () => onSelectDriver && onSelectDriver(p.mac) }}
          >
            <Tooltip permanent direction="top" offset={[0, -8]} className="tl-sat-tag">{p.tag}</Tooltip>
          </CircleMarker>
        </React.Fragment>
      ))}
    </MapContainer>
  );
}

const emptyLapBuffers = () => ({
  speed: [], accelX: [], accelY: [], accelZ: [],
  gLat: [], gLong: [], totalG: [], lateralG: [], brakeAccel: [],
  gyroX: [], gyroY: [], gyroZ: [],
  rpm: [], throttle: [], brake: [],
});

const fmtElapsed = (sec) => {
  if (sec == null) return '00:00';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

export default function TrainingLive({ token, trainingId, publicMode = false, onStop = () => {} }) {
  const [snapshot, setSnapshot] = useState(null);
  const [initCircuit, setInitCircuit] = useState(null);
  const [selectedMac, setSelectedMac] = useState(null);
  const [directionAlerts, setDirectionAlerts] = useState([]);
  const [denied, setDenied] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [showStopConfirm, setShowStopConfirm] = useState(false);
  const [followSelected, setFollowSelected] = useState(false);
  const session = useSession();
  const navigate = useNavigate();
  const [satellite, setSatellite] = useState(false); // vista satellitare (solo allenamento)
  // Mappa a schermo intero: API del browser; dove non c'è (iPhone) la mappa
  // occupa comunque tutta la finestra.
  const mapPanelRef = useRef(null);
  const [nativeFs, setNativeFs] = useState(false);
  const [pseudoFs, setPseudoFs] = useState(false);
  const fullscreen = nativeFs || pseudoFs;
  const fullscreenRef = useRef(false);   // letto dalla rotella del canvas
  useEffect(() => { fullscreenRef.current = fullscreen; }, [fullscreen]);
  useEffect(() => {
    const onChange = () => {
      const el = document.fullscreenElement || document.webkitFullscreenElement || null;
      setNativeFs(!!el && el === mapPanelRef.current);
    };
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, []);
  useEffect(() => {
    if (!pseudoFs) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setPseudoFs(false); };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [pseudoFs]);
  const toggleFullscreen = useCallback(() => {
    const el = mapPanelRef.current;
    if (!el) return;
    const active = document.fullscreenElement || document.webkitFullscreenElement;
    if (active || pseudoFs) {
      if (active) {
        try {
          const p = (document.exitFullscreen || document.webkitExitFullscreen).call(document);
          if (p && p.catch) p.catch(() => {});
        } catch { /* noop */ }
      }
      setPseudoFs(false);
      return;
    }
    const req = el.requestFullscreen || el.webkitRequestFullscreen;
    if (!req) { setPseudoFs(true); return; }
    try {
      const p = req.call(el);
      if (p && p.catch) p.catch(() => setPseudoFs(true));
    } catch { setPseudoFs(true); }
  }, [pseudoFs]);
  const [blink, setBlink] = useState(true);

  // refs telemetria/mappa
  const snapshotRef = useRef(null);
  useEffect(() => { snapshotRef.current = snapshot; }, [snapshot]);
  const telemRef = useRef(new Map());
  const obdRef = useRef(new Map()); // mac → ultimi dati CAN (oggi non ricevuti)
  // mac → cronometro del giro e delta live calcolati dal server (frame 15 Hz)
  const timingRef = useRef(new Map());
  const renderPosRef = useRef(new Map());
  const trailsRef = useRef({});
  const drPhaseRef = useRef(new Map());
  const colorsRef = useRef({});
  const geomFetchedRef = useRef(null); // fetch-once geometria completa circuito
  const lastAnimTsRef = useRef(performance.now());

  // dati per-pilota (buffer per-giro + buffer rolling per G-G)
  const lapDataByMacRef = useRef(new Map());
  const telemBufferByMacRef = useRef(new Map());

  // ref attivi passati al pannello (puntano al pilota selezionato)
  const selectedLapDataRef = useRef({ current: emptyLapBuffers(), currentLapNum: 0, currentDist: 0, prev: null, prevLapNum: null, lapHistory: [] });
  const selectedTelemBufferRef = useRef({ gLat: [], gLong: [] });
  const selectedCompareLapRef = useRef(null);
  const hoverRef = useRef({ dist: null, lat: null, lon: null, chartRect: null });
  const [compareLapNum, setCompareLapNum] = useState(null);
  const [instructorOverrides, setInstructorOverrides] = useState({});
  const [selectedLiveSample, setSelectedLiveSample] = useState(null);

  const canvasRef = useRef(null);
  const animFrameRef = useRef(null);
  const zoomRef = useRef(1);
  const zoomByRef = useRef(null);        // pulsanti + e − della mappa circuito
  const panRef = useRef({ x: 0, y: 0 });
  const pointersRef = useRef(new Map());
  const pinchStartRef = useRef({ dist: 0, zoom: 1, panX: 0, panY: 0, midX: 0, midY: 0 });
  const dirtyRef = useRef(false);
  const selectedMacRef = useRef(null);
  useEffect(() => { selectedMacRef.current = selectedMac; }, [selectedMac]);
  const followSelectedRef = useRef(false);
  useEffect(() => { followSelectedRef.current = followSelected; }, [followSelected]);

  const TRAIL_MAX_AGE_MS = 2000;
  const TRAIL_MAX_LEN = 15;
  const TRAIL_MIN_MOVE_M = 2.5;
  const BUFFER_DURATION_MS = 60000;
  const BUFFER_MAX_SAMPLES = 900;
  // Cap di sicurezza dei buffer del giro CORRENTE (anti-lag su giri lunghi /
  // rilevamento giro assente). ~2400 campioni = oltre 2,5 min a 15 Hz.
  const CURRENT_LAP_MAX = 2400;
  const CURRENT_TRAIL_MAX = 1600;

  useEffect(() => { const t = setInterval(() => setBlink(b => !b), 700); return () => clearInterval(t); }, []);

  // Un solo campione condiviso a 10 Hz: valori istantanei del pilota
  // selezionato e, per tutti i piloti, cronometro del giro e delta live.
  // Il cronometro avanza fra un frame e l'altro con l'orologio locale, ma parte
  // sempre dal valore del server (niente sfasamenti fra orologi).
  const [liveTiming, setLiveTiming] = useState({});
  useEffect(() => {
    let timer;
    const sample = () => {
      const mac = selectedMacRef.current;
      const live = mac ? telemRef.current.get(mac) : null;
      const obd = mac ? obdRef.current.get(mac) : null;
      setSelectedLiveSample(mac ? { mac, speedKmh: live?.speedKmh ?? null, gforce: live?.gforce || null, obd: obd || null } : null);
      const now = performance.now();
      const out = {};
      timingRef.current.forEach((t, m) => {
        const clock = t.lapElapsed == null ? null : t.lapElapsed + Math.min(2, (now - t.recvAt) / 1000);
        out[m] = {
          clock,
          delta: t.liveDelta,
          deltaRef: t.deltaRef,
          last: t.lastLapDelta,
          lastFresh: !!t.lastLapDelta && now - (t.lastSeenAt || 0) < 7000,
        };
      });
      setLiveTiming(out);
      timer = setTimeout(sample, 100);
    };
    sample(); return () => clearTimeout(timer);
  }, []);

  const ensureMacData = useCallback((mac) => {
    if (!lapDataByMacRef.current.has(mac)) {
      lapDataByMacRef.current.set(mac, {
        currentLapStart: Date.now(),
        currentLapNum: 0,
        prevLapNum: null,
        currentDist: 0,
        distInitialized: false,
        lastLat: null, lastLon: null,
        current: emptyLapBuffers(),
        prev: null,
        lapHistory: [],
        currentTrail: [],
        prevTrail: [],
        _waitingForFirstCrossing: undefined,
      });
    }
    if (!telemBufferByMacRef.current.has(mac)) {
      telemBufferByMacRef.current.set(mac, { gLat: [], gLong: [] });
    }
  }, []);

  const pushTelem = useCallback((mac, key, ts, value) => {
    const buf = telemBufferByMacRef.current.get(mac);
    if (!buf || !buf[key]) return;
    buf[key].push({ ts, value });
    const cutoff = ts - BUFFER_DURATION_MS;
    while (buf[key].length > 0 && buf[key][0].ts < cutoff) buf[key].shift();
    if (buf[key].length > BUFFER_MAX_SAMPLES) buf[key].splice(0, buf[key].length - BUFFER_MAX_SAMPLES);
  }, []);

  const handleNewLap = useCallback((mac, newLapCount) => {
    const ld = lapDataByMacRef.current.get(mac);
    if (!ld || newLapCount <= ld.currentLapNum) return;
    if (ld.currentLapNum > 0 && ld.current.speed.length > 10) {
      if (!ld.lapHistory.some(l => l.lapNum === ld.currentLapNum)) {
        ld.lapHistory.push({
          ...ld.current,
          totalDist: ld.currentDist,
          lapNum: ld.currentLapNum,
          trail: ld.currentTrail.slice(),
        });
        if (ld.lapHistory.length > 50) ld.lapHistory.shift();
      }
    }
    ld.prev = { ...ld.current, totalDist: ld.currentDist };
    ld.prevLapNum = ld.currentLapNum;
    ld.prevTrail = ld.currentTrail;
    ld.current = emptyLapBuffers();
    ld.currentTrail = [];
    ld.currentLapStart = Date.now();
    ld.currentLapNum = newLapCount;
    ld.currentDist = 0;
    ld.distInitialized = true;
  }, []);

  const normalizeLap = useCallback((lap) => {
    if (!lap) return null;
    return {
      ...emptyLapBuffers(),
      ...lap,
      lapNum: Number(lap.lapNum) || 0,
      totalDist: Number(lap.totalDist) || 0,
      trail: Array.isArray(lap.trail) ? lap.trail.slice() : [],
      speed: Array.isArray(lap.speed) ? lap.speed.slice() : [],
      accelX: Array.isArray(lap.accelX) ? lap.accelX.slice() : [],
      accelY: Array.isArray(lap.accelY) ? lap.accelY.slice() : [],
      accelZ: Array.isArray(lap.accelZ) ? lap.accelZ.slice() : [],
      gLat: Array.isArray(lap.gLat) ? lap.gLat.slice() : [],
      gLong: Array.isArray(lap.gLong) ? lap.gLong.slice() : [],
      totalG: Array.isArray(lap.totalG) ? lap.totalG.slice() : [],
      lateralG: Array.isArray(lap.lateralG) ? lap.lateralG.slice() : [],
      brakeAccel: Array.isArray(lap.brakeAccel) ? lap.brakeAccel.slice() : [],
      gyroX: Array.isArray(lap.gyroX) ? lap.gyroX.slice() : [],
      gyroY: Array.isArray(lap.gyroY) ? lap.gyroY.slice() : [],
      gyroZ: Array.isArray(lap.gyroZ) ? lap.gyroZ.slice() : [],
      rpm: Array.isArray(lap.rpm) ? lap.rpm.slice() : [],
      throttle: Array.isArray(lap.throttle) ? lap.throttle.slice() : [],
      brake: Array.isArray(lap.brake) ? lap.brake.slice() : [],
      lapTime: lap.lapTime ?? null,
      partial: !!lap.partial,
    };
  }, []);

  const hydrateDriverTelemetry = useCallback((driver) => {
    if (!driver?.mac) return;
    ensureMacData(driver.mac);
    const ld = lapDataByMacRef.current.get(driver.mac);
    if (!ld) return;

    if (Array.isArray(driver.lapHistory)) {
      const merged = new Map((ld.lapHistory || []).map(l => [Number(l.lapNum), l]));
      driver.lapHistory.forEach(raw => {
        const num = Number(raw?.lapNum);
        if (!num) return;
        const existing = merged.get(num);
        const hasData = Array.isArray(raw.speed) && raw.speed.length > 0;
        if (!hasData) {
          // Metadato dallo snapshot leggero 1Hz: aggiorna tempo/flag senza
          // degradare gli array già accumulati (live o fetch on-demand).
          if (existing) {
            if (raw.lapTime != null) existing.lapTime = raw.lapTime;
            existing.partial = !!raw.partial;
            if (!existing.totalDist && raw.totalDist) existing.totalDist = raw.totalDist;
            // turno di guida del giro (auto con i cambi pilota)
            if (raw.stint != null) existing.stint = raw.stint;
            if (raw.swap) existing.swap = true;
          } else {
            merged.set(num, normalizeLap(raw));
          }
          return;
        }
        // Giro con telemetria completa: non sostituire un giro locale più
        // ricco (accumulato a 15Hz) con una versione decimata dal server.
        if (existing && Array.isArray(existing.speed) && existing.speed.length > raw.speed.length) {
          if (raw.lapTime != null) existing.lapTime = raw.lapTime;
          if (raw.stint != null) existing.stint = raw.stint;
          if (raw.swap) existing.swap = true;
          return;
        }
        merged.set(num, normalizeLap(raw));
      });
      ld.lapHistory = Array.from(merged.values()).sort((a, b) => a.lapNum - b.lapNum).slice(-50);
      const lastCompleted = ld.lapHistory[ld.lapHistory.length - 1] || null;
      if (lastCompleted) {
        ld.prev = lastCompleted;
        ld.prevLapNum = lastCompleted.lapNum;
        ld.prevTrail = Array.isArray(lastCompleted.trail) ? lastCompleted.trail.slice() : [];
      }
    }

    // Giro corrente: idrata solo se il server ha PIÙ dati del locale (client
    // appena collegato / resync). A regime i buffer locali a 15Hz sono più
    // ricchi della versione decimata e non vanno degradati.
    const serverCurrent = normalizeLap(driver.currentLap);
    if (serverCurrent && serverCurrent.speed.length > (ld.current?.speed?.length || 0)) {
      ld.current = {
        ...emptyLapBuffers(),
        speed: serverCurrent.speed,
        accelX: serverCurrent.accelX,
        accelY: serverCurrent.accelY,
        accelZ: serverCurrent.accelZ,
        gLat: serverCurrent.gLat,
        gLong: serverCurrent.gLong,
        totalG: serverCurrent.totalG,
        lateralG: serverCurrent.lateralG,
        brakeAccel: serverCurrent.brakeAccel,
        gyroX: serverCurrent.gyroX,
        gyroY: serverCurrent.gyroY,
        gyroZ: serverCurrent.gyroZ,
        rpm: serverCurrent.rpm,
        throttle: serverCurrent.throttle,
        brake: serverCurrent.brake,
      };
      ld.currentDist = serverCurrent.totalDist || 0;
      ld.currentTrail = Array.isArray(serverCurrent.trail) ? serverCurrent.trail.slice() : [];
      ld.currentLapNum = Number(driver.lapCount) || Number(serverCurrent.lapNum) || ld.currentLapNum || 0;
      ld.distInitialized = true;
      const lastTrail = ld.currentTrail[ld.currentTrail.length - 1];
      if (lastTrail) {
        ld.lastLat = lastTrail.lat;
        ld.lastLon = lastTrail.lon;
      }
    } else if (Number(driver.lapCount) > ld.currentLapNum) {
      ld.currentLapNum = Number(driver.lapCount) || ld.currentLapNum;
    }

    const tb = telemBufferByMacRef.current.get(driver.mac);
    if (tb && serverCurrent) {
      const now = Date.now();
      tb.gLat = (serverCurrent.gLat || []).slice(-120).map((p, idx) => ({ ts: now - (serverCurrent.gLat.length - idx) * 100, value: p.value || 0 }));
      tb.gLong = (serverCurrent.gLong || []).slice(-120).map((p, idx) => ({ ts: now - (serverCurrent.gLong.length - idx) * 100, value: p.value || 0 }));
    }

    if (selectedMacRef.current === driver.mac) {
      selectedLapDataRef.current = ld;
      selectedTelemBufferRef.current = telemBufferByMacRef.current.get(driver.mac);
      selectedCompareLapRef.current = compareLapNum == null
        ? ld.prev
        : (ld.lapHistory || []).find(l => l.lapNum === compareLapNum) || null;
    }
  }, [compareLapNum, ensureMacData, normalizeLap]);

  // Aggiorna i ref attivi quando cambia il pilota selezionato o il giro di confronto
  useEffect(() => {
    if (!selectedMac) return;
    ensureMacData(selectedMac);
    selectedLapDataRef.current = lapDataByMacRef.current.get(selectedMac);
    selectedTelemBufferRef.current = telemBufferByMacRef.current.get(selectedMac);
  }, [selectedMac, ensureMacData]);

  // Telemetria completa on-demand: lo snapshot 1Hz porta solo i metadati dei
  // giri (anti-lag), quindi alla prima selezione di un pilota scarichiamo una
  // volta la sua storia completa (array decimati) via REST.
  const hydratedMacsRef = useRef(new Set());
  useEffect(() => {
    if (!selectedMac || !token || hydratedMacsRef.current.has(selectedMac)) return undefined;
    hydratedMacsRef.current.add(selectedMac);
    let alive = true;
    fetch(`${API_BASE}/api/training/pilot/${encodeURIComponent(selectedMac)}?token=${encodeURIComponent(token)}`)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error('fetch pilota fallito'))))
      .then(j => { if (alive && j?.pilot) hydrateDriverTelemetry(j.pilot); })
      .catch(() => { hydratedMacsRef.current.delete(selectedMac); /* ritenta alla prossima selezione */ });
    return () => { alive = false; };
  }, [selectedMac, token, hydrateDriverTelemetry]);

  useEffect(() => {
    const ld = selectedLapDataRef.current;
    if (!ld) { selectedCompareLapRef.current = null; return; }
    if (compareLapNum == null) selectedCompareLapRef.current = ld.prev;
    else selectedCompareLapRef.current = ld.lapHistory.find(l => l.lapNum === compareLapNum) || null;
  }, [compareLapNum, selectedMac, snapshot]);

  useEffect(() => {
    const d = (snapshot?.drivers || []).find(x => x.mac === selectedMac);
    if (d?.instructorLapNum != null) setCompareLapNum(Number(d.instructorLapNum));
  }, [snapshot, selectedMac]);

  const circuit = initCircuit || snapshot?.circuit || null;

  // === WebSocket ===
  useEffect(() => {
    if (!token) return;
    const ws = new WebSocket(WS_URL + '?role=viewer');
    let alive = true;

    ws.onopen = () => {
      try { ws.send(JSON.stringify({ type: 'training_subscribe', token })); } catch { }
    };

    ws.onmessage = async (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data?.type === 'training_denied') { if (alive) setDenied(true); return; }

        if (data?.type === 'training_init') {
          // NON sovrascrivere la geometria completa già caricata (customSectors/
          // bordi/cordoli) con la versione ridotta dell'init: causava il "flash"
          // del circuito corretto seguito dal disegno semplificato.
          if (data.circuit?.sectors?.length) {
            setInitCircuit(prev => {
              if (prev && prev.id === data.circuit.id &&
                ((prev.customSectors?.length) || (prev.leftBorder?.length) || (prev.kerbs?.length))) {
                return prev;
              }
              return data.circuit;
            });
          }
          return;
        }

        if (data?.type === 'training_snapshot') {
          (data.drivers || []).forEach(d => {
            const keyName = d.fullName || d.tag || d.mac;
            if (!colorsRef.current[d.mac]) colorsRef.current[d.mac] = colorFromName(keyName);
            ensureMacData(d.mac);
            hydrateDriverTelemetry(d);
          });
          if (data.circuit?.id && geomFetchedRef.current !== data.circuit.id) {
            geomFetchedRef.current = data.circuit.id;
            try {
              const rc = await fetch(`${API_BASE}/api/circuits/${data.circuit.id}`);
              if (rc.ok) {
                const full = await rc.json();
                setInitCircuit(prev => ({ ...(prev || {}), id: data.circuit.id, name: full.name || data.circuit.id, stats: full.stats || {}, params: full.params || {}, sectors: full.sectors || [], customSectors: full.customSectors || [], leftBorder: full.leftBorder || [], rightBorder: full.rightBorder || [], kerbs: full.kerbs || [], corners: full.corners || [] }));
              }
            } catch { }
          }
          if (alive) {
            setSnapshot(data);
            // auto-seleziona il primo pilota
            setSelectedMac(prev => prev || (data.drivers?.[0]?.mac ?? null));
          }
          dirtyRef.current = true;
          return;
        }

        if (data?.type === 'training_telemetry' && Array.isArray(data.drivers)) {
          const now = Date.now();
          const present = new Set();
          data.drivers.forEach(d => {
            if (!d || !d.mac) return;
            present.add(d.mac);
            if (!Number.isFinite(d.lat) || !Number.isFinite(d.lon)) return;
            telemRef.current.set(d.mac, { lat: d.lat, lon: d.lon, speedKmh: d.speedKmh || 0, gforce: d.gforce || null, ts: data.ts || now });
            {
              const prevT = timingRef.current.get(d.mac);
              const lastKey = d.lastLapDelta ? `${d.lastLapDelta.lapNum}:${d.lastLapDelta.at}` : null;
              timingRef.current.set(d.mac, {
                lapElapsed: d.lapElapsed ?? null,
                recvAt: performance.now(),
                liveDelta: d.liveDelta ?? null,
                deltaRef: d.deltaRef || null,
                lastLapDelta: d.lastLapDelta || null,
                lastKey,
                // il "delta finale" si mostra per qualche secondo dopo il traguardo
                lastSeenAt: prevT && prevT.lastKey === lastKey ? prevT.lastSeenAt : (prevT ? performance.now() : 0),
              });
            }
            drPhaseRef.current.set(d.mac, { phase: d.drPhase || 'LIVE', lastSeenMs: d.lastSeenMs || 0, alpha: (d.drAlpha != null) ? d.drAlpha : 1 });
            if (!colorsRef.current[d.mac]) colorsRef.current[d.mac] = colorFromName(d.mac);
            ensureMacData(d.mac);

            // trail rolling per mappa
            const t = trailsRef.current[d.mac] || [];
            const last = t[t.length - 1];
            if (!last || haversine(last.lat, last.lon, d.lat, d.lon) >= TRAIL_MIN_MOVE_M) {
              const next = [...t, { lat: d.lat, lon: d.lon, ts: now }];
              trailsRef.current[d.mac] = next.length > TRAIL_MAX_LEN ? next.slice(-TRAIL_MAX_LEN) : next;
            }

            // buffer per-giro
            const ld = lapDataByMacRef.current.get(d.mac);
            if (ld) {
              if (d.lapCount != null && d.lapCount > ld.currentLapNum) handleNewLap(d.mac, d.lapCount);
              if (!ld.distInitialized && circuit?.sectors?.length >= 2) {
                ld.currentDist = findDistOnCircuit(d.lat, d.lon, circuit.sectors);
                ld.distInitialized = true;
                ld.current = emptyLapBuffers();
              }
              if (ld.lastLat != null && ld.lastLon != null) {
                const delta = haversine(ld.lastLat, ld.lastLon, d.lat, d.lon);
                if (delta > 0.5 && delta < 200) ld.currentDist += delta;
              }
              ld.lastLat = d.lat; ld.lastLon = d.lon;
              const dist = ld.currentDist;
              const elapsed = (Date.now() - ld.currentLapStart) / 1000;
              const push = (key, value) => { if (ld.current[key]) ld.current[key].push({ t: dist, value, elapsed }); };
              push('speed', d.speedKmh || 0);
              if (d.accel) { push('accelX', d.accel.x || 0); push('accelY', d.accel.y || 0); push('accelZ', d.accel.z || 0); }
              if (d.gyro) { push('gyroX', d.gyro.x || 0); push('gyroY', d.gyro.y || 0); push('gyroZ', d.gyro.z || 0); }
              if (d.gforce) {
                push('gLat', d.gforce.lat || 0); push('gLong', d.gforce.long || 0);
                push('totalG', d.gforce.total || 0); push('lateralG', Math.abs(d.gforce.lat || 0));
                push('brakeAccel', d.gforce.long || 0);
                pushTelem(d.mac, 'gLat', now, d.gforce.lat || 0);
                pushTelem(d.mac, 'gLong', now, d.gforce.long || 0);
              }
              if (d.obd) {
                obdRef.current.set(d.mac, d.obd);
                push('rpm', d.obd.rpm || 0);
                push('throttle', d.obd.throttle || 0);
                push('brake', d.obd.brake || 0);
              }
              // trail completo del giro corrente
              const ct = ld.currentTrail;
              const lp = ct[ct.length - 1];
              if (!lp || haversine(lp.lat, lp.lon, d.lat, d.lon) >= TRAIL_MIN_MOVE_M) {
                ct.push({ lat: d.lat, lon: d.lon, speed: d.speedKmh || 0 });
              }

              // SALVAGUARDIA ANTI-LAG: i buffer del giro corrente si azzerano solo
              // a fine giro. Se un giro è molto lungo o il rilevamento giro non
              // scatta, crescerebbero all'infinito (→ lag progressivo, solo in
              // allenamento). Quando superano la soglia, li decimo (1 punto su 2):
              // la forma del giro resta, il costo di rendering/memoria resta limitato.
              if (ld.current.speed && ld.current.speed.length > CURRENT_LAP_MAX) {
                for (const k of Object.keys(ld.current)) {
                  const a = ld.current[k];
                  if (Array.isArray(a) && a.length > 2) {
                    const out = [];
                    for (let i = 0; i < a.length; i += 2) out.push(a[i]);
                    ld.current[k] = out;
                  }
                }
              }
              if (ld.currentTrail.length > CURRENT_TRAIL_MAX) {
                const out = [];
                for (let i = 0; i < ld.currentTrail.length; i += 2) out.push(ld.currentTrail[i]);
                ld.currentTrail = out;
              }
            }
          });
          // rimuovi marker dei mac spariti (GONE)
          Array.from(telemRef.current.keys()).forEach(mac => {
            if (!present.has(mac)) {
              telemRef.current.delete(mac);
              trailsRef.current[mac] = [];
              drPhaseRef.current.set(mac, { phase: 'GONE', lastSeenMs: 12000, alpha: 0 });
            }
          });
          // ANTI-LAG: i frame telemetria arrivano a ~15 Hz. Aggiornare lo stato
          // (e quindi ri-renderizzare TrainingLive + i grafici pesanti) ad ogni
          // frame era la causa principale del rallentamento progressivo. La mappa
          // è già fluida da sola (rAF che legge i ref); aggiorniamo lo stato React
          // SOLO quando gli alert direzione gara cambiano davvero.
          if (!publicMode && Array.isArray(data.directionAlerts)) {
            setDirectionAlerts(prev => {
              const next = data.directionAlerts;
              if (prev.length === next.length &&
                prev.every((a, i) => a.mac === next[i].mac && a.level === next[i].level)) {
                return prev; // nessun cambiamento → niente re-render
              }
              return next;
            });
          }
          dirtyRef.current = true;
          return;
        }

        if (data?.type === 'training_inactive') {
          if (alive) { setSnapshot(s => s ? { ...s, _ended: true } : s); }
          return;
        }
      } catch { }
    };

    ws.onerror = (e) => console.error('[TrainingLive] WS error', e);
    return () => { alive = false; try { ws.send(JSON.stringify({ type: 'training_unsubscribe' })); ws.close(); } catch { } };
  }, [token, ensureMacData, handleNewLap, hydrateDriverTelemetry, pushTelem, publicMode, initCircuit?.sectors?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // === Canvas mappa multi-pilota ===
  useEffect(() => {
    // In vista satellite il canvas è smontato: salta. La dipendenza da `satellite`
    // ri-esegue l'effetto al toggle, riagganciandosi al nuovo elemento canvas.
    if (satellite) return;
    if (!circuit || !canvasRef.current || !circuit?.sectors?.length) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d', { alpha: true });
    const activePointers = pointersRef.current;
    const dpr = window.devicePixelRatio || 1;
    canvas.style.touchAction = 'none';

    const ensureSize = () => {
      const rect = canvas.getBoundingClientRect();
      const w = rect.width || canvas.parentElement.clientWidth || 600;
      const h = rect.height || canvas.parentElement.clientHeight || 520;
      if (canvas.width !== Math.floor(w * dpr) || canvas.height !== Math.floor(h * dpr)) {
        canvas.width = Math.floor(w * dpr); canvas.height = Math.floor(h * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      }
    };
    const onResize = () => ensureSize();
    ensureSize();
    window.addEventListener('resize', onResize);
    let ro = null;
    let resizeRaf = null;
    const scheduleEnsureSize = () => {
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(() => {
        resizeRaf = null;
        ensureSize();
      });
    };
    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(scheduleEnsureSize); ro.observe(canvas); }

    const lats = circuit.sectors.map(s => s.lat);
    const lons = circuit.sectors.map(s => s.lon);
    const minLat = Math.min(...lats), maxLat = Math.max(...lats);
    const minLon = Math.min(...lons), maxLon = Math.max(...lons);
    const centerLat = (minLat + maxLat) / 2, centerLon = (minLon + maxLon) / 2;
    const R = 6371000, centerLatRad = centerLat * Math.PI / 180;
    const dxList = lons.map(l => (l - centerLon) * Math.PI / 180 * R * Math.cos(centerLatRad));
    const dyList = lats.map(l => (l - centerLat) * Math.PI / 180 * R);
    const maxDx = Math.max(...dxList.map(Math.abs));
    const maxDy = Math.max(...dyList.map(Math.abs));
    const padding = 26;   // margine interno: stretto, il circuito deve riempire

    const project = (lat, lon) => {
      const w = canvas.width / dpr, h = canvas.height / dpr;
      const maxRange = Math.max(maxDx, maxDy) * 1.04;
      const scale = Math.min(w - padding * 2, h - padding * 2) / (maxRange * 2) * zoomRef.current;
      const dx = (lon - centerLon) * Math.PI / 180 * R * Math.cos(centerLatRad);
      const dy = (lat - centerLat) * Math.PI / 180 * R;
      return { x: w / 2 + dx * scale + panRef.current.x, y: h / 2 - dy * scale + panRef.current.y, scale };
    };

    const clampViewport = () => {
      const W = canvas.clientWidth || canvas.width / dpr;
      const H = canvas.clientHeight || canvas.height / dpr;
      const maxRange = Math.max(maxDx, maxDy) * 1.04 || 1;
      const baseScale = Math.max(0.0001, Math.min(W - padding * 2, H - padding * 2) / (maxRange * 2));
      const contentX = maxDx * baseScale * zoomRef.current;
      const contentY = maxDy * baseScale * zoomRef.current;
      const keepVisible = 70;
      const limitX = Math.max(0, W / 2 + contentX - keepVisible);
      const limitY = Math.max(0, H / 2 + contentY - keepVisible);
      panRef.current.x = Math.max(-limitX, Math.min(limitX, panRef.current.x));
      panRef.current.y = Math.max(-limitY, Math.min(limitY, panRef.current.y));
    };
    const stopFollowing = () => {
      if (!followSelectedRef.current) return;
      followSelectedRef.current = false;
      setFollowSelected(false);
    };
    const wheel = (e) => {
      // fuori dallo schermo intero la rotella fa scorrere la pagina (zoom con + e −)
      if (!fullscreenRef.current) return;
      e.preventDefault();
      stopFollowing();
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left, my = e.clientY - rect.top;
      const W = rect.width, H = rect.height;
      const oldZoom = zoomRef.current;
      const dir = e.deltaY > 0 ? 0.88 : 1.12;
      const newZoom = Math.max(0.5, Math.min(40, oldZoom * dir));
      const ratio = newZoom / oldZoom;
      panRef.current.x = panRef.current.x * ratio + (mx - W / 2) * (1 - ratio);
      panRef.current.y = panRef.current.y * ratio + (my - H / 2) * (1 - ratio);
      zoomRef.current = newZoom;
      clampViewport();
    };
    // pulsanti + e −: zoom attorno al centro della mappa
    zoomByRef.current = (dir) => {
      stopFollowing();
      const oldZoom = zoomRef.current;
      const newZoom = Math.max(0.5, Math.min(40, oldZoom * (dir > 0 ? 1.3 : 1 / 1.3)));
      const ratio = newZoom / oldZoom;
      panRef.current.x *= ratio;
      panRef.current.y *= ratio;
      zoomRef.current = newZoom;
      clampViewport();
    };
    const lastMouse = { x: 0, y: 0 };
    const onPointerDown = (ev) => {
      stopFollowing();
      canvas.setPointerCapture?.(ev.pointerId);
      activePointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
      if (activePointers.size === 1) { lastMouse.x = ev.clientX; lastMouse.y = ev.clientY; }
      else if (activePointers.size === 2) {
        const pts = Array.from(activePointers.values());
        const rect = canvas.getBoundingClientRect();
        pinchStartRef.current = {
          dist: Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y),
          zoom: zoomRef.current, panX: panRef.current.x, panY: panRef.current.y,
          midX: (pts[0].x + pts[1].x) / 2 - rect.left,
          midY: (pts[0].y + pts[1].y) / 2 - rect.top,
        };
      }
      canvas.style.cursor = 'grabbing';
      ev.preventDefault();
    };
    const onPointerMove = (ev) => {
      if (!activePointers.has(ev.pointerId)) return;
      activePointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
      if (activePointers.size === 1) {
        const p = activePointers.get(ev.pointerId);
        panRef.current.x += p.x - lastMouse.x; panRef.current.y += p.y - lastMouse.y;
        lastMouse.x = p.x; lastMouse.y = p.y;
        clampViewport();
      } else if (activePointers.size === 2) {
        const pts = Array.from(activePointers.values());
        const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
        const start = pinchStartRef.current;
        if (start.dist > 0) {
          const rect = canvas.getBoundingClientRect();
          const mx = (pts[0].x + pts[1].x) / 2 - rect.left;
          const my = (pts[0].y + pts[1].y) / 2 - rect.top;
          const newZoom = Math.max(0.5, Math.min(40, start.zoom * (dist / start.dist)));
          const zr = newZoom / start.zoom;
          // Mantiene sotto le dita lo stesso punto del circuito e include anche
          // la traslazione del centro del pinch: niente drift cumulativo.
          panRef.current.x = start.panX * zr + (mx - rect.width / 2) - (start.midX - rect.width / 2) * zr;
          panRef.current.y = start.panY * zr + (my - rect.height / 2) - (start.midY - rect.height / 2) * zr;
          zoomRef.current = newZoom;
          clampViewport();
        }
      }
      ev.preventDefault();
    };
    const onPointerUp = (ev) => {
      canvas.releasePointerCapture?.(ev.pointerId);
      activePointers.delete(ev.pointerId);
      pinchStartRef.current.dist = 0;
      if (activePointers.size === 1) {
        // Fondamentale: il dito rimasto diventa la nuova origine del pan.
        // Senza questo reset il primo movimento successivo produce un salto.
        const remaining = Array.from(activePointers.values())[0];
        lastMouse.x = remaining.x; lastMouse.y = remaining.y;
      } else if (activePointers.size === 0) canvas.style.cursor = 'grab';
    };
    const resetViewport = (ev) => {
      ev.preventDefault(); stopFollowing();
      zoomRef.current = 1; panRef.current = { x: 0, y: 0 };
      activePointers.clear(); pinchStartRef.current.dist = 0;
    };
    canvas.style.cursor = 'grab';
    canvas.addEventListener('wheel', wheel, { passive: false });
    canvas.addEventListener('pointerdown', onPointerDown, { passive: false });
    canvas.addEventListener('pointermove', onPointerMove, { passive: false });
    canvas.addEventListener('pointerup', onPointerUp);
    canvas.addEventListener('pointercancel', onPointerUp);
    canvas.addEventListener('pointerleave', onPointerUp);
    canvas.addEventListener('dblclick', resetViewport);

    const colorToRgbaLocal = (color, alpha) => {
      if (color.startsWith('#')) {
        const hex = color.slice(1);
        const full = hex.length === 3 ? hex.split('').map(c => c + c).join('') : hex;
        const v = parseInt(full, 16);
        return `rgba(${(v >> 16) & 255},${(v >> 8) & 255},${v & 255},${alpha})`;
      }
      return color;
    };

    const draw = () => {
      ensureSize();
      const w = canvas.width / dpr, h = canvas.height / dpr;
      const cs = getComputedStyle(document.documentElement);
      const bg = cs.getPropertyValue('--rs-canvas-bg').trim() || '#0a0a0e';
      const trackCol = cs.getPropertyValue('--rs-canvas-track').trim() || 'rgba(80,84,90,0.95)';
      const outline = cs.getPropertyValue('--rs-canvas-outline').trim() || 'rgba(255,255,255,0.10)';
      const textCol = cs.getPropertyValue('--rs-canvas-text').trim() || '#fff';
      const textShadow = cs.getPropertyValue('--rs-canvas-text-shadow').trim() || 'rgba(0,0,0,0.8)';

      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = bg; ctx.fillRect(0, 0, w, h);

      const widthMeters = circuit.params?.widthMeters ?? 6;
      const pts = circuit.sectors.map(s => project(s.lat, s.lon));
      const scale = pts[0]?.scale || 1;
      const trackPx = Math.max(6, widthMeters * scale);

      // Disegno pista IDENTICO alla modalità gara (RaceLive): banda manuale fra
      // i bordi, oppure centerline spessa con settori colorati S1/S2/S3, cordoli
      // e segni di settore.
      const customSec = circuit.customSectors || [];
      const lB = circuit.leftBorder, rB = circuit.rightBorder;
      const hasManualBorders = Array.isArray(lB) && lB.length > 2 && Array.isArray(rB) && rB.length > 2;

      if (hasManualBorders) {
        // === Circuito MANUALE: asfalto = banda piena fra i due bordi (larghezza reale) ===
        const lp = lB.map(p => project(p.lat, p.lon));
        const rp = rB.map(p => project(p.lat, p.lon));
        ctx.beginPath();
        lp.forEach((p, i) => i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y));
        for (let i = rp.length - 1; i >= 0; i--) ctx.lineTo(rp[i].x, rp[i].y);
        ctx.closePath();
        ctx.fillStyle = trackCol; ctx.fill();
        ctx.strokeStyle = outline; ctx.lineWidth = 1.5;
        ctx.beginPath(); lp.forEach((p, i) => i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)); ctx.closePath(); ctx.stroke();
        ctx.beginPath(); rp.forEach((p, i) => i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)); ctx.closePath(); ctx.stroke();
      } else {
        // === LEGACY (circuiti senza bordi): settori + asfalto spessi sulla centerline ===
        if (Array.isArray(customSec) && customSec.length > 0) {
          customSec.forEach((sector, idx) => {
            if (sector.startIdx == null || sector.endIdx == null) return;
            const start = Math.max(0, Math.min(sector.startIdx, circuit.sectors.length - 1));
            const end = Math.max(0, Math.min(sector.endIdx, circuit.sectors.length - 1));
            if (end <= start) return;
            const color = sector.color || ['#ff0000', '#00ff00', '#0000ff'][idx % 3];
            ctx.save();
            ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.strokeStyle = color; ctx.lineWidth = trackPx + 3; ctx.globalAlpha = 0.65;
            ctx.beginPath();
            for (let i = start; i <= end; i++) {
              const p = project(circuit.sectors[i].lat, circuit.sectors[i].lon);
              if (i === start) ctx.moveTo(p.x, p.y); else ctx.lineTo(p.x, p.y);
            }
            ctx.stroke(); ctx.restore();
          });
        } else {
          ctx.strokeStyle = outline; ctx.lineWidth = trackPx + 3; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
          ctx.beginPath(); pts.forEach((p, i) => i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)); ctx.closePath(); ctx.stroke();
        }
        ctx.strokeStyle = trackCol; ctx.lineWidth = trackPx; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
        ctx.beginPath(); pts.forEach((p, i) => i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)); ctx.closePath(); ctx.stroke();
      }
      // Cordoli (linee rosso/bianco, sottili)
      if (Array.isArray(circuit.kerbs)) {
        circuit.kerbs.forEach(k => {
          const kp = (k.points || []).map(p => project(p.lat, p.lon));
          if (kp.length < 2) return;
          const seq = k.closed ? [...kp, kp[0]] : kp;
          const kw = Math.max(2, trackPx * 0.12);
          for (let i = 1; i < seq.length; i++) {
            ctx.beginPath(); ctx.moveTo(seq[i - 1].x, seq[i - 1].y); ctx.lineTo(seq[i].x, seq[i].y);
            ctx.strokeStyle = i % 2 === 0 ? '#e10600' : '#ffffff'; ctx.lineWidth = kw; ctx.lineCap = 'butt';
            ctx.stroke();
          }
        });
      }
      // Settori: segno colorato trasversale dove FINISCE ogni settore (solo manuali)
      if (hasManualBorders && Array.isArray(customSec) && customSec.length && circuit.sectors?.length) {
        const n = circuit.sectors.length;
        customSec.forEach((sector, idx) => {
          if (sector.endIdx == null) return;
          const e = Math.max(0, Math.min(sector.endIdx, n - 1));
          const a = circuit.sectors[Math.max(0, e - 1)], b = circuit.sectors[Math.min(n - 1, e + 1)];
          const pe = project(circuit.sectors[e].lat, circuit.sectors[e].lon);
          const pa = project(a.lat, a.lon), pb = project(b.lat, b.lon);
          let tx = pb.x - pa.x, ty = pb.y - pa.y; const tl = Math.hypot(tx, ty) || 1; tx /= tl; ty /= tl;
          const nx = -ty, ny = tx;
          const half = Math.max(5, trackPx * 0.55);
          ctx.save();
          ctx.strokeStyle = sector.color || ['#ff0000', '#00ff00', '#0000ff'][idx % 3];
          ctx.lineWidth = Math.max(2, Math.min(5, trackPx * 0.09)); ctx.lineCap = 'round';
          ctx.beginPath(); ctx.moveTo(pe.x - nx * half, pe.y - ny * half); ctx.lineTo(pe.x + nx * half, pe.y + ny * half); ctx.stroke();
          ctx.restore();
        });
      }
      // Numeri curva FUORI dal tracciato, in prossimità della curva
      if (Array.isArray(circuit.corners) && circuit.corners.length && circuit.sectors?.length && pts.length) {
        const n = circuit.sectors.length;
        let cgx = 0, cgy = 0;
        for (const p of pts) { cgx += p.x; cgy += p.y; }
        cgx /= pts.length; cgy /= pts.length;
        const ordered = [...circuit.corners].sort((A, B) => A.startIdx - B.startIdx);
        ctx.font = '700 11px "JetBrains Mono", monospace'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ordered.forEach((c, i) => {
          const mid = (c.startIdx <= c.endIdx) ? Math.floor((c.startIdx + c.endIdx) / 2) : c.startIdx;
          const s = circuit.sectors[Math.max(0, Math.min(n - 1, mid))]; if (!s) return;
          const p = project(s.lat, s.lon);
          let ox = p.x - cgx, oy = p.y - cgy; const ol = Math.hypot(ox, oy) || 1; ox /= ol; oy /= ol;
          const off = Math.max(14, trackPx * 0.6 + 12);
          const lx = p.x + ox * off, ly = p.y + oy * off;
          ctx.beginPath(); ctx.arc(lx, ly, 9, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(10,10,14,0.82)'; ctx.fill();
          ctx.strokeStyle = '#C0FF03'; ctx.lineWidth = 1.5; ctx.stroke();
          ctx.fillStyle = '#C0FF03'; ctx.fillText(String(i + 1), lx, ly + 0.5);
        });
        ctx.textBaseline = 'alphabetic';
      }

      // linea start
      if (pts.length > 1) {
        const s0 = pts[0], s1 = pts[1]; const ang = Math.atan2(s1.y - s0.y, s1.x - s0.x);
        ctx.save(); ctx.translate(s0.x, s0.y); ctx.rotate(ang);
        ctx.strokeStyle = '#fff'; ctx.lineWidth = Math.max(2, trackPx * 0.12);
        ctx.beginPath(); ctx.moveTo(0, -trackPx * 0.5); ctx.lineTo(0, trackPx * 0.5); ctx.stroke(); ctx.restore();
      }

      const selMac = selectedMacRef.current;

      const selectedTrailLineWidth = Math.max(2.5, trackPx * 0.15);
      const selectedTrails = [];
      if (selMac) {
        const ld = lapDataByMacRef.current.get(selMac);
        if (ld?.prevTrail?.length > 1) selectedTrails.push(ld.prevTrail);
        if (ld?.currentTrail?.length > 1) selectedTrails.push(ld.currentTrail);
      }

      let speedMin = Infinity;
      let speedMax = -Infinity;
      selectedTrails.forEach(trail => {
        trail.forEach(pt => {
          const speed = Number(pt.speed);
          if (!Number.isFinite(speed)) return;
          speedMin = Math.min(speedMin, speed);
          speedMax = Math.max(speedMax, speed);
        });
      });
      if (!Number.isFinite(speedMin) || !Number.isFinite(speedMax) || speedMin === speedMax) {
        speedMin = 0;
        speedMax = 100;
      }
      const speedRange = speedMax - speedMin;
      const speedLow = speedMin + speedRange * 0.05;
      const speedHigh = speedMax - speedRange * 0.05;
      const speedToColor = (speed, alpha = 1) => {
        const t = Math.max(0, Math.min(1, ((Number(speed) || 0) - speedLow) / (speedHigh - speedLow || 1)));
        let r;
        let g;
        if (t < 0.5) {
          r = 255;
          g = Math.round(255 * (t / 0.5));
        } else {
          r = Math.round(255 * (1 - (t - 0.5) / 0.5));
          g = 255;
        }
        return `rgba(${r},${g},0,${alpha})`;
      };
      const drawSpeedTrail = (trail, alpha = 1) => {
        if (!trail || trail.length <= 1) return;
        ctx.save();
        ctx.lineWidth = selectedTrailLineWidth;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        for (let t = 1; t < trail.length; t++) {
          const prev = project(trail[t - 1].lat, trail[t - 1].lon);
          const cur = project(trail[t].lat, trail[t].lon);
          ctx.strokeStyle = speedToColor(trail[t].speed, alpha);
          ctx.beginPath();
          ctx.moveTo(prev.x, prev.y);
          ctx.lineTo(cur.x, cur.y);
          ctx.stroke();
        }
        ctx.restore();
      };

      // traiettorie complete solo del pilota selezionato, colorate per velocità
      if (selMac) {
        const ld = lapDataByMacRef.current.get(selMac);
        const sd = (snapshotRef.current?.drivers || []).find(d => d.mac === selMac);
        const ov = instructorOverrides[selMac];
        const refNum = ov?.instructorLapNum ?? sd?.instructorLapNum;
        const refMode = ov?.instructorTrailMode ?? sd?.instructorTrailMode ?? 'blue';
        const refLap = (ld?.lapHistory || []).find(l => Number(l.lapNum) === Number(refNum));
        const referenceTrail = refLap?.trail?.length > 1
          ? refLap.trail
          : (Number(ld?.prevLapNum) === Number(refNum) ? ld?.prevTrail : null);

        drawSpeedTrail(ld?.prevTrail, 0.24);
        drawSpeedTrail(ld?.currentTrail, 0.82);

        // Il riferimento istruttore resta sopra ogni altra traiettoria.
        if (referenceTrail?.length > 1) {
          if (refMode === 'speed') {
            // Casing continuo azzurro: identifica il riferimento anche quando
            // la heatmap usa gli stessi colori della traiettoria allievo.
            ctx.save();
            ctx.strokeStyle = '#063f63'; ctx.lineWidth = selectedTrailLineWidth + 6; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
            ctx.beginPath(); referenceTrail.forEach((pt, i) => { const p = project(pt.lat, pt.lon); i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y); }); ctx.stroke();
            ctx.strokeStyle = '#25c8ff'; ctx.lineWidth = selectedTrailLineWidth + 3;
            ctx.stroke();
            ctx.restore();
            drawSpeedTrail(referenceTrail, 1);
          } else {
            ctx.save();
            ctx.shadowColor = 'rgba(18, 191, 255, .55)'; ctx.shadowBlur = 5;
            ctx.strokeStyle = '#12bfff'; ctx.lineWidth = selectedTrailLineWidth + 2.5; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
            ctx.beginPath(); referenceTrail.forEach((pt, i) => { const p = project(pt.lat, pt.lon); i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y); }); ctx.stroke();
            ctx.restore();
          }
        }
      }

      // trail rolling + marker per ogni pilota
      const now = Date.now();
      Object.keys(trailsRef.current).forEach(k => {
        trailsRef.current[k] = (trailsRef.current[k] || []).filter(p => now - p.ts <= TRAIL_MAX_AGE_MS).slice(-TRAIL_MAX_LEN);
      });

      const drivers = snapshotRef.current?.drivers || [];
      drivers.forEach(d => {
        const drInfo = drPhaseRef.current.get(d.mac);
        const phase = drInfo?.phase || 'LIVE';
        if (phase === 'GONE') return;
        const smoothed = renderPosRef.current.get(d.mac);
        const live = telemRef.current.get(d.mac);
        const lat = smoothed?.lat ?? live?.lat ?? d.lat;
        const lon = smoothed?.lon ?? live?.lon ?? d.lon;
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
        const color = colorsRef.current[d.mac] || colorFromName(d.fullName || d.tag || d.mac);
        const isSel = d.mac === selMac;

        const p = project(lat, lon);
        const r = Math.min(Math.max(3, trackPx * 0.10), 11) + (isSel ? 2 : 0);
        const alpha = (drInfo?.alpha != null) ? drInfo.alpha : 1;
        ctx.save();
        ctx.globalAlpha = phase === 'FADEOUT' ? Math.max(0.2, alpha) : 1;
        ctx.shadowColor = 'rgba(0,0,0,0.35)'; ctx.shadowBlur = 2;
        ctx.fillStyle = colorToRgbaLocal(color, 1); ctx.beginPath(); ctx.arc(p.x, p.y, r, 0, Math.PI * 2); ctx.fill();
        ctx.shadowBlur = 0; ctx.strokeStyle = isSel ? '#fff' : (d.inPit ? '#f59e0b' : 'rgba(255,255,255,0.7)'); ctx.lineWidth = isSel ? 3 : 2; ctx.stroke();
        ctx.fillStyle = textCol; ctx.strokeStyle = textShadow; ctx.lineWidth = 2.5;
        ctx.font = '700 10px "JetBrains Mono", monospace'; ctx.textAlign = 'center';
        ctx.strokeText(d.tag, p.x, p.y - (r + 8)); ctx.fillText(d.tag, p.x, p.y - (r + 8));
        ctx.restore();
      });
    };

    const animate = () => {
      const now = performance.now();
      const dt = Math.min(50, now - lastAnimTsRef.current);
      lastAnimTsRef.current = now;
      const drivers = snapshotRef.current?.drivers || [];
      const present = new Set(drivers.map(d => d.mac));
      drivers.forEach(d => {
        const live = telemRef.current.get(d.mac);
        const targetLat = live?.lat ?? d.lat;
        const targetLon = live?.lon ?? d.lon;
        if (!Number.isFinite(targetLat) || !Number.isFinite(targetLon)) return;
        const prev = renderPosRef.current.get(d.mac) || { lat: targetLat, lon: targetLon };
        const k = 1 - Math.pow(1 - 0.18, dt / 16);
        renderPosRef.current.set(d.mac, { lat: prev.lat + (targetLat - prev.lat) * k, lon: prev.lon + (targetLon - prev.lon) * k });
      });
      if (followSelectedRef.current && selectedMacRef.current) {
        const selectedDriver = drivers.find(d => d.mac === selectedMacRef.current);
        const smoothed = renderPosRef.current.get(selectedMacRef.current);
        const live = telemRef.current.get(selectedMacRef.current);
        const lat = smoothed?.lat ?? live?.lat ?? selectedDriver?.lat;
        const lon = smoothed?.lon ?? live?.lon ?? selectedDriver?.lon;
        if (Number.isFinite(lat) && Number.isFinite(lon)) {
          const w = canvas.width / dpr;
          const h = canvas.height / dpr;
          const p = project(lat, lon);
          const followK = 1 - Math.pow(1 - 0.28, dt / 16);
          panRef.current.x += (w / 2 - p.x) * followK;
          panRef.current.y += (h / 2 - p.y) * followK;
        }
      }
      Array.from(renderPosRef.current.keys()).forEach(mac => { if (!present.has(mac)) renderPosRef.current.delete(mac); });
      draw();
      animFrameRef.current = requestAnimationFrame(animate);
    };

    draw();
    animFrameRef.current = requestAnimationFrame(animate);
    return () => {
      window.removeEventListener('resize', onResize);
      if (ro) ro.disconnect();
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      canvas.removeEventListener('wheel', wheel);
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', onPointerUp);
      canvas.removeEventListener('pointercancel', onPointerUp);
      canvas.removeEventListener('pointerleave', onPointerUp);
      canvas.removeEventListener('dblclick', resetViewport);
      zoomByRef.current = null;
      activePointers.clear(); pinchStartRef.current.dist = 0;
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [circuit, satellite, instructorOverrides]);

  // Etichette giri: POST al backend (token-gated) + merge ottimistico locale,
  // così l'etichetta appare subito senza aspettare il prossimo snapshot WS.
  const [labelOverrides, setLabelOverrides] = useState({}); // mac → { lapNum: label }
  const handleLabelLap = useCallback(async (lapNum, label) => {
    const mac = selectedMacRef.current;
    if (!mac) return false;
    try {
      const r = await fetch(`${API_BASE}/api/training/lap-label`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, mac, lapNum, label }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.error || 'Errore salvataggio');
      setLabelOverrides(prev => ({ ...prev, [mac]: j.lapLabels || {} }));
      return true;
    } catch (e) {
      console.error('[TrainingLive] lap-label error', e.message);
      return false;
    }
  }, [token]);

  const handleInstructorLap = useCallback(async (lapNum, trailMode) => {
    const mac = selectedMacRef.current;
    if (!mac) return false;
    try {
      const r = await fetch(`${API_BASE}/api/training/instructor-lap`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token, mac, lapNum, trailMode }) });
      const j = await r.json(); if (!r.ok) throw new Error(j?.error || 'Errore salvataggio');
      setInstructorOverrides(prev => ({ ...prev, [mac]: j }));
      setCompareLapNum(j.instructorLapNum);
      if (j.instructorLapNum != null) {
        const detail = await fetch(`${API_BASE}/api/training/pilot/${encodeURIComponent(mac)}?token=${encodeURIComponent(token)}`);
        if (detail.ok) {
          const payload = await detail.json();
          if (payload?.pilot) hydrateDriverTelemetry(payload.pilot);
        }
      }
      return true;
    } catch (e) { console.error('[TrainingLive] instructor-lap error', e.message); return false; }
  }, [token, hydrateDriverTelemetry]);

  // ── Auto con i cambi pilota: anagrafica piloti, cambio pilota, correzione di un turno ──
  const [pilotsList, setPilotsList] = useState([]);
  // anagrafica piloti: all'apertura, tornando sulla scheda e aprendo "Cambia
  // pilota" (un cliente appena creato in Partecipanti compare subito)
  const loadPilots = useCallback(() => {
    if (publicMode) return;
    fetch(`${API_BASE}/api/pilots`).then(r => (r.ok ? r.json() : null)).then(j => { if (Array.isArray(j)) setPilotsList(j); }).catch(() => { });
  }, [publicMode]);
  useEffect(() => {
    loadPilots();
    window.addEventListener('focus', loadPilots);
    return () => window.removeEventListener('focus', loadPilots);
  }, [loadPilots]);
  const applyCrew = useCallback((mac, j) => {
    setSnapshot(s => (s?.drivers ? { ...s, drivers: s.drivers.map(d => (d.mac === mac ? { ...d, pilot: j.pilot, stints: j.stints } : d)) } : s));
  }, []);
  const postCrew = useCallback(async (path, body) => {
    try {
      const r = await fetch(`${API_BASE}${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) return { ok: false, error: j?.error || 'Operazione non riuscita' };
      applyCrew(body.mac, j);
      return j;
    } catch {
      return { ok: false, error: 'Connessione non riuscita, riprova' };
    }
  }, [applyCrew]);
  const handleChangePilot = useCallback((mac, pilotId) => postCrew('/api/training/driver-change', { mac, pilotId }), [postCrew]);
  const handleFixStint = useCallback((mac, n, pilotId) => postCrew('/api/training/stint-pilot', { mac, stint: n, pilotId }), [postCrew]);

  const handleStop = async () => {
    setStopping(true);
    try {
      await fetch(`${API_BASE}/api/training/stop`, { method: 'POST' });
      onStop(trainingId);
    } catch (e) {
      alert('Errore terminazione: ' + e.message);
    } finally {
      setStopping(false);
      setShowStopConfirm(false);
    }
  };

  const drivers = snapshot?.drivers || [];
  const selectedDriver = drivers.find(d => d.mac === selectedMac) || null;
  const brandColor = selectedMac ? (colorsRef.current[selectedMac] || '#C0FF03') : '#C0FF03';
  const bestOverall = drivers
    .map(d => d.bestLapTime)
    .filter(v => Number.isFinite(v))
    .sort((a, b) => a - b)[0] ?? null;
  const totalCompletedLaps = drivers.reduce((acc, d) => acc + (Number(d.lapCount) || 0), 0);
  const selectedColor = selectedDriver
    ? (colorsRef.current[selectedDriver.mac] || colorFromName(selectedDriver.fullName || selectedDriver.tag || selectedDriver.mac))
    : brandColor;
  // piloti alla guida delle altre auto (non possono salire su questa)
  const busyPilots = new Map(drivers
    .filter(d => d.mac !== selectedMac && d.car && d.pilot?.id)
    .map(d => [String(d.pilot.id), d.car.name || d.fullName]));

  if (denied) {
    return (
      <div className="rs-live-root tl-root">
        <div className="rs-empty tl-denied">
          <Icon.Lock size={28} />
          <strong>Accesso negato</strong>
          <p>Token non valido o sessione non più attiva.</p>
        </div>
      </div>
    );
  }

  const drLevelClass = (phase) => {
    if (phase === 'GONE') return 'tl-dr tl-dr--gone';
    if (phase === 'FADEOUT' || phase === 'MODEL' || phase === 'COAST') return 'tl-dr tl-dr--warn';
    return 'tl-dr';
  };
  const selTiming = selectedDriver ? liveTiming[selectedDriver.mac] || null : null;
  const selLive = selectedLiveSample?.mac === selectedDriver?.mac ? selectedLiveSample : null;

  return (
    <div className="rs-live-root tl-root">
      {/* Alert segnale (Dead Reckoning) — utile durante i test */}
      {!publicMode && directionAlerts.length > 0 && (
        <div className="tl-alerts">
          {directionAlerts.slice(0, 5).map(a => (
            <div key={a.mac + a.level} className={`tl-alert ${a.level === 'gone' ? 'tl-alert--gone' : a.level === 'lost' ? 'tl-alert--late' : ''}`}>
              <div className="tl-alert__head">
                <span className="tl-alert__dot" />
                <strong>{a.fullName || a.tag || a.mac}</strong>
                <span>{((a.lastSeenMs || 0) / 1000).toFixed(1)}s</span>
              </div>
              <div className="tl-alert__sub">
                {a.level === 'gone' ? 'Segnale perso' : a.level === 'lost' ? 'Segnale in dissolvenza' : 'Dati in ritardo'}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Barra sessione */}
      <header className="tl-topbar">
        <div className="tl-topbar__status">
          <span className="tl-live"><span className={`tl-live__dot ${blink ? 'is-on' : ''}`} />{snapshot?._ended ? 'Terminata' : 'Allenamento'}</span>
        </div>
        <dl className="tl-facts">
          <div className="tl-fact tl-fact--circuit"><dt>Circuito</dt><dd title={circuit?.name || ''}>{circuit?.name || '—'}</dd></div>
          <div className="tl-fact"><dt>Sessione</dt><dd className="tl-mono">{fmtElapsed(snapshot?.elapsedSeconds)}</dd></div>
          <div className="tl-fact"><dt>Piloti</dt><dd className="tl-mono">{drivers.length}</dd></div>
          <div className="tl-fact"><dt>Giri</dt><dd className="tl-mono">{totalCompletedLaps}</dd></div>
          <div className="tl-fact"><dt>Best sessione</dt><dd className="tl-mono is-best">{formatLap(bestOverall)}</dd></div>
          {!publicMode && snapshot?.liveViewers != null && (
            <div className="tl-fact"><dt>Spettatori</dt><dd className="tl-mono">{snapshot.liveViewers}</dd></div>
          )}
        </dl>
        <div className="tl-topbar__right">
          {/* aperta dal link ma con accesso alla piattaforma: torna alla vista operatore */}
          {publicMode && session && (
            <button className="rs-btn rs-btn--sm rs-btn--ghost" onClick={() => navigate('/allenamento')} title="Torna alla sessione nella piattaforma">
              <Icon.ArrowLeft size={15} /><span className="tl-btn-label">Allenamento</span>
            </button>
          )}
          {!publicMode && (
            <button className="rs-btn rs-btn--sm rs-btn--danger" onClick={() => setShowStopConfirm(true)} disabled={stopping || snapshot?._ended}>
              <Icon.X size={15} /><span className="tl-btn-label">Termina</span>
            </button>
          )}
          {/* nella piattaforma il tema si cambia dalla barra in alto; il link pubblico ha il suo */}
          {publicMode && <ThemeToggle />}
        </div>
      </header>

      {/* Conferma stop */}
      {showStopConfirm && (
        <div className="rs-modal-backdrop" onClick={() => setShowStopConfirm(false)}>
          <div className="rs-modal" onClick={e => e.stopPropagation()}>
            <div className="rs-modal__head"><div className="rs-modal__title">Terminare l'allenamento?</div></div>
            <div className="rs-modal__body">La sessione verrà salvata: la ritrovi in Allenamenti passati, con replay e analisi.</div>
            <div className="rs-modal__foot">
              <button className="rs-btn rs-btn--ghost" onClick={() => setShowStopConfirm(false)}>Annulla</button>
              <button className="rs-btn rs-btn--danger" onClick={handleStop} disabled={stopping}>{stopping ? 'Terminazione…' : 'Termina e salva'}</button>
            </div>
          </div>
        </div>
      )}

      <section className="tl-stage">
        <div className={`tl-map ${fullscreen ? 'is-fullscreen' : ''} ${pseudoFs ? 'is-pseudo-fs' : ''}`} ref={mapPanelRef}>
          <div className="tl-panel-head">
            {fullscreen ? (
              <span className="tl-fs-facts">
                <b>{circuit?.name || 'Allenamento'}</b>
                <span>Sessione <b className="tl-mono">{fmtElapsed(snapshot?.elapsedSeconds)}</b></span>
                <span>Best <b className="tl-mono is-best">{formatLap(bestOverall)}</b></span>
              </span>
            ) : <span className="tl-panel-title">Track view</span>}
            <div className="tl-map__actions">
              <div className="tl-legend" aria-hidden="true">
                <span><i className="tl-legend__line" />Velocità giro</span>
                <span><i className="tl-legend__ref" />Riferimento</span>
              </div>
              <button
                type="button"
                className={`tl-tool ${followSelected ? 'is-active' : ''}`}
                onClick={() => setFollowSelected(v => !v)}
                disabled={!selectedMac}
                title={selectedMac ? 'Segui automaticamente il pilota selezionato' : 'Seleziona un pilota da seguire'}
              >
                <Icon.Radio size={14} /><span className="tl-btn-label">{followSelected ? 'Seguendo' : 'Segui'}</span>
              </button>
              <button
                type="button"
                className={`tl-tool ${satellite ? 'is-active' : ''}`}
                onClick={() => setSatellite(v => !v)}
                title={satellite ? 'Torna alla vista circuito' : 'Mostra la vista satellitare'}
              >
                {satellite ? <Icon.Track size={14} /> : <Icon.Layers size={14} />}
                <span className="tl-btn-label">{satellite ? 'Circuito' : 'Satellite'}</span>
              </button>
              <button
                type="button"
                className={`tl-tool tl-tool--fs ${fullscreen ? 'is-active' : ''}`}
                onClick={toggleFullscreen}
                title={fullscreen ? 'Esci dallo schermo intero (Esc)' : 'Mappa a schermo intero'}
                aria-label={fullscreen ? 'Esci dallo schermo intero' : 'Mappa a schermo intero'}
              >
                {fullscreen ? <Icon.Minimize size={14} /> : <Icon.Maximize size={14} />}
                <span className="tl-btn-label">{fullscreen ? 'Esci' : 'Schermo intero'}</span>
              </button>
            </div>
          </div>
          <div className="tl-map__stage">
            {satellite ? (
              <TrainingSatelliteMap
                circuit={circuit}
                drivers={snapshot?.drivers || []}
                selectedMac={selectedMac}
                onSelectDriver={setSelectedMac}
                followSelected={followSelected}
                liveRefs={{ telemRef, trailsRef, colorsRef, drPhaseRef }}
                wheelZoom={fullscreen}
              />
            ) : (
              <>
                <canvas ref={canvasRef} className="track-canvas tl-map__canvas" />
                <div className="tl-zoom" role="group" aria-label="Zoom della mappa">
                  <button type="button" onClick={() => zoomByRef.current?.(1)} title="Ingrandisci" aria-label="Ingrandisci">+</button>
                  <button type="button" onClick={() => zoomByRef.current?.(-1)} title="Riduci" aria-label="Riduci">−</button>
                </div>
              </>
            )}

            {/* Cronometro e delta del pilota selezionato */}
            {selectedDriver && (
              <div className="tl-hud" style={{ '--driver-color': selectedColor }}>
                <div className="tl-hud__head">
                  <span className="tl-hud__swatch" />
                  <b>{selectedDriver.tag || selectedDriver.mac}</b>
                  <span className="tl-hud__name">{selectedDriver.car ? [selectedDriver.pilot?.name, selectedDriver.fullName].filter(Boolean).join(' · ') : (selectedDriver.fullName || selectedDriver.team || '')}</span>
                  <span className="tl-hud__lap">L{(selectedDriver.lapCount || 0) + 1}</span>
                </div>
                <div className="tl-hud__clock">
                  <span>{selTiming?.clock == null ? '—:—.-' : fmtLapTime(selTiming.clock, 1)}</span>
                  <em>{selLive?.speedKmh != null ? `${Math.round(selLive.speedKmh)} km/h` : ''}</em>
                </div>
                <DeltaReadout
                  size="lg"
                  value={selTiming?.delta ?? null}
                  refInfo={selTiming?.deltaRef || null}
                  last={selTiming?.last || null}
                  lastFresh={!!selTiming?.lastFresh}
                  note={(selectedDriver.lapCount || 0) > 0 ? 'Delta in attesa di posizione' : 'Delta dopo il primo giro completo'}
                />
                <div className="tl-hud__laps">
                  <span><small>Ultimo</small><b>{formatLap(selectedDriver.lastLapTime)}</b></span>
                  <span><small>Best</small><b className="is-best">{formatLap(selectedDriver.bestLapTime)}</b></span>
                </div>
              </div>
            )}

            {/* Schermo intero: piloti in pista in forma compatta (clic = seleziona) */}
            {fullscreen && drivers.length > 0 && (
              <div className="tl-fs-roster">
                <div className="tl-fs-roster__head"><span>Piloti</span><span>Giri</span><span>Ultimo</span><span>Best</span><span>Delta</span></div>
                {drivers.map(d => {
                  const color = colorsRef.current[d.mac] || colorFromName(d.fullName || d.tag || d.mac);
                  const t = liveTiming[d.mac];
                  const dv = t?.lastFresh ? t.last?.value : t?.delta;
                  const isBest = bestOverall != null && d.bestLapTime === bestOverall;
                  return (
                    <button
                      type="button"
                      key={d.mac}
                      className={`tl-fs-row ${d.mac === selectedMac ? 'is-selected' : ''}`}
                      style={{ '--driver-color': color }}
                      onClick={() => setSelectedMac(d.mac)}
                    >
                      <span className="tl-fs-row__who"><i />{d.tag || d.mac}{d.inPit && <em>PIT</em>}</span>
                      <span className="tl-mono">{d.lapCount ?? 0}</span>
                      <span className="tl-mono">{formatLap(d.lastLapTime)}</span>
                      <span className={`tl-mono ${isBest ? 'is-best' : ''}`}>{formatLap(d.bestLapTime)}</span>
                      <span className={`tl-mono ${dv == null ? '' : dv < -0.0005 ? 'is-faster' : dv > 0.0005 ? 'is-slower' : ''}`}>{dv == null ? '—' : fmtSigned(dv, 2)}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <aside className="tl-roster">
          <div className="tl-panel-head">
            <span className="tl-panel-title">Piloti in pista</span>
            <span className="tl-count">{drivers.length}</span>
          </div>
          <div className="tl-roster__list">
            {drivers.length === 0 ? (
              <div className="tl-empty">In attesa dei dati GPS…</div>
            ) : (
              <table className="tl-table">
                <thead>
                  <tr>
                    <th>Pilota</th>
                    <th className="num">Giri</th>
                    <th className="num tl-col-last">Ultimo</th>
                    <th className="num">Best</th>
                    <th className="num">Delta</th>
                  </tr>
                </thead>
                <tbody>
                  {drivers.map(d => {
                    const isSel = d.mac === selectedMac;
                    const color = colorsRef.current[d.mac] || colorFromName(d.fullName || d.tag || d.mac);
                    const isBest = bestOverall != null && d.bestLapTime === bestOverall;
                    const t = liveTiming[d.mac];
                    const dv = t?.lastFresh ? t.last?.value : t?.delta;
                    return (
                      <tr
                        key={d.mac}
                        className={`tl-row ${isSel ? 'is-selected' : ''}`}
                        style={{ '--driver-color': color }}
                        onClick={() => setSelectedMac(d.mac)}
                        title="Mostra la telemetria del pilota"
                      >
                        <td>
                          <span className="tl-row__who">
                            <span className="tl-row__color" />
                            <span className="tl-row__tag">{d.tag || d.mac}</span>
                            <span className={drLevelClass(d.drPhase)} title={`Segnale: ${d.drPhase || 'LIVE'}`} />
                            {d.inPit && <span className="tl-pit">PIT</span>}
                          </span>
                          {d.car
                            ? <span className="tl-row__name" title={`${d.fullName} · alla guida ${d.pilot?.name || '—'}`}><b>{d.pilot?.name || '—'}</b> · {d.fullName}</span>
                            : <span className="tl-row__name">{d.fullName || d.team || d.mac}</span>}
                        </td>
                        <td className="num tl-mono">{d.lapCount ?? 0}</td>
                        <td className="num tl-mono tl-col-last">{formatLap(d.lastLapTime)}</td>
                        <td className={`num tl-mono ${isBest ? 'is-best' : ''}`}>{formatLap(d.bestLapTime)}</td>
                        <td className={`num tl-mono tl-row__delta ${dv == null ? '' : dv < -0.0005 ? 'is-faster' : dv > 0.0005 ? 'is-slower' : ''}`}>
                          {dv == null ? '—' : fmtSigned(dv, 2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          <div className="tl-roster__foot">Delta live rispetto al best del pilota (o al giro istruttore, se scelto).</div>
        </aside>
      </section>

      <div className="tl-analysis">
        {/* Auto con i cambi pilota: chi guida, cambio pilota, turni e report */}
        {selectedDriver?.car && (
          <TrainingCrew
            driver={selectedDriver}
            pilots={pilotsList}
            onOpenEdit={loadPilots}
            busyPilots={busyPilots}
            onChangePilot={!publicMode && !snapshot?._ended ? handleChangePilot : null}
            onFixStint={!publicMode && !snapshot?._ended ? handleFixStint : null}
            live={!snapshot?._ended}
          />
        )}
        {/* Analisi del pilota selezionato: timing, dinamica IMU, CAN, device */}
        <TrainingPilotPanel
          driver={selectedDriver ? {
            ...selectedDriver,
            lapLabels: { ...(selectedDriver.lapLabels || {}), ...(labelOverrides[selectedDriver.mac] || {}) },
            ...(instructorOverrides[selectedDriver.mac] || {}),
            ...(selLive ? {
              speedKmh: selLive.speedKmh ?? selectedDriver.speedKmh,
              gforce: selLive.gforce || selectedDriver.gforce,
              obd: selLive.obd || selectedDriver.obd,
            } : {}),
          } : null}
          lapHistory={selectedLapDataRef.current?.lapHistory || []}
          compareLapNum={compareLapNum}
          onChangeCompareLap={setCompareLapNum}
          lapDataRef={selectedLapDataRef}
          telemetryBufferRef={selectedTelemBufferRef}
          compareLapRef={selectedCompareLapRef}
          hoverRef={hoverRef}
          brandColor={selectedColor}
          obdRef={obdRef}
          canLabel={!publicMode && !snapshot?._ended}
          onLabelLap={handleLabelLap}
          instructorLapNum={(instructorOverrides[selectedDriver?.mac] || {}).instructorLapNum ?? selectedDriver?.instructorLapNum ?? null}
          instructorTrailMode={(instructorOverrides[selectedDriver?.mac] || {}).instructorTrailMode ?? selectedDriver?.instructorTrailMode ?? 'blue'}
          onSetInstructorLap={!publicMode ? handleInstructorLap : null}
          timing={selTiming}
        />
      </div>

    </div>
  );
}
